import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  useTenantPartnersEnabled,
  useUpdateTenantPartnersEnabled,
} from "../hooks/tenant-partners-admin.hooks";
import { TenantPartnerCommissionControl } from "./tenant-partners-section/TenantPartnerCommissionControl";

// Tasks 0028/0029, contract 2026-09-28 — Guesteria-staff-only (SUPER_ADMIN /
// GUESTERIA_ADMIN) tenant controls: the Guesteria Partners integration
// toggle and the tenant's commission share. Rendered from `TenantEditPage`
// on the tenant-info tab, gated by `isPlatformAdmin` there.
interface TenantPartnersSectionProps {
  tenantId: string;
}

export const TenantPartnersSection: React.FC<TenantPartnersSectionProps> = ({
  tenantId,
}) => {
  const { t } = useTranslation();

  const {
    data: partnersSettings,
    isLoading: isPartnersEnabledLoading,
    isError: isPartnersEnabledError,
  } = useTenantPartnersEnabled(tenantId, true);
  const { mutate: updatePartnersEnabled, isPending: isSavingPartnersEnabled } =
    useUpdateTenantPartnersEnabled(tenantId);

  const handleToggle = (checked: boolean) => {
    updatePartnersEnabled(
      { enabled: checked },
      {
        onSuccess: () => {
          toast.success(
            t(
              "tenants.detail.partners.enableSuccess",
              "Guesteria Partners updated",
            ),
          );
        },
        onError: () => {
          toast.error(t("tenants.detail.partners.enableError"));
        },
      },
    );
  };

  return (
    <div className="bg-background flex flex-col gap-4 rounded-2xl border p-6">
      <h2 className="text-lg font-semibold">
        {t("tenants.detail.partners.title")}
      </h2>

      <div className="flex items-center justify-between rounded-xl border p-4">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">
            {t("tenants.detail.partners.enabledLabel")}
          </p>
          {isPartnersEnabledError ? (
            <p className="text-custom-destructive-dark text-sm">
              {t("tenants.detail.partners.loadError")}
            </p>
          ) : null}
        </div>
        {isPartnersEnabledLoading ? (
          <div
            className="bg-muted h-5 w-9 animate-pulse rounded-full"
            aria-label={t("tenants.detail.partners.loading", "Loading…")}
          />
        ) : (
          <Switch
            checked={Boolean(partnersSettings?.enabled)}
            onCheckedChange={handleToggle}
            disabled={isSavingPartnersEnabled}
          />
        )}
      </div>

      <TenantPartnerCommissionControl tenantId={tenantId} />
    </div>
  );
};
